import { Type, type Static } from "@sinclair/typebox";
const uuid = Type.String({ format: "uuid" }),
  date = Type.String({ format: "date" }),
  nullableDate = Type.Union([date, Type.Null()]);
export const RecurringRuleFields = {
  unit: Type.Union(
    ["day", "week", "month", "year"].map((v) => Type.Literal(v)),
  ),
  interval: Type.Integer({ minimum: 1, maximum: 365 }),
  endOn: nullableDate,
  maxOccurrences: Type.Union([
    Type.Integer({ minimum: 1, maximum: 10000 }),
    Type.Null(),
  ]),
  employeeId: Type.Union([uuid, Type.Null()]),
  durationDays: Type.Integer({ minimum: 1, maximum: 120 }),
  billingMode: Type.Union(
    ["per-intervention", "every-n", "periodic"].map((v) => Type.Literal(v)),
  ),
  billingEvery: Type.Integer({ minimum: 1, maximum: 120 }),
  billingMonths: Type.Integer({ minimum: 1, maximum: 120 }),
  amount: Type.Number({ minimum: 0, maximum: 99999999999.99 }),
};
export const RecurringCreateSchema = Type.Object(
  {
    operationId: uuid,
    condominiumId: uuid,
    plannedHours: Type.Optional(
      Type.Union([
        Type.Number({ minimum: 0, maximum: 999999999999.99 }),
        Type.Null(),
      ]),
    ),
    title: Type.String({ minLength: 1, maxLength: 240 }),
    contactPhone: Type.Optional(
      Type.Union([Type.String({ maxLength: 80 }), Type.Null()]),
    ),
    ...RecurringRuleFields,
  },
  { additionalProperties: false },
);
export const RecurringUpdateSchema = Type.Object(
  {
    operationId: uuid,
    version: Type.Integer({ minimum: 1 }),
    contactPhone: Type.Optional(
      Type.Union([Type.String({ maxLength: 80 }), Type.Null()]),
    ),
    jobVersion: Type.Optional(Type.Integer({ minimum: 1 })),
    ...RecurringRuleFields,
    status: Type.Union(
      ["active", "suspended", "ended"].map((v) => Type.Literal(v)),
    ),
  },
  { additionalProperties: false },
);
export const RecurringScheduleSchema = Type.Object(
  {
    operationId: uuid,
    version: Type.Integer({ minimum: 1 }),
    date,
    employeeId: Type.Union([uuid, Type.Null()]),
    durationDays: Type.Integer({ minimum: 1, maximum: 120 }),
  },
  { additionalProperties: false },
);
export const RecurringOccurrenceSchema = Type.Object(
  {
    operationId: uuid,
    version: Type.Integer({ minimum: 1 }),
    date,
    employeeId: Type.Union([uuid, Type.Null()]),
    durationDays: Type.Integer({ minimum: 1, maximum: 120 }),
    scope: Type.Union([Type.Literal("one"), Type.Literal("following")]),
    action: Type.Union(
      ["reschedule", "complete", "cancel"].map((v) => Type.Literal(v)),
    ),
  },
  { additionalProperties: false },
);
export const RecurringBillingSchema = Type.Object(
  {
    operationId: uuid,
    version: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);
export const RecurringInvoiceSchema = Type.Object(
  {
    operationId: uuid,
    invoiceId: uuid,
  },
  { additionalProperties: false },
);
export type RecurringCreate = Static<typeof RecurringCreateSchema>;
export type RecurringUpdate = Static<typeof RecurringUpdateSchema>;
export type RecurringSchedule = Static<typeof RecurringScheduleSchema>;
export type RecurringOccurrenceChange = Static<
  typeof RecurringOccurrenceSchema
>;
export interface RecurringPlan {
  contactPhone?: string | null;
  jobVersion?: number;
  id: string;
  jobId: string;
  fascicleId: string | null;
  kind: "maintenance" | "lifeline";
  code: string;
  title: string;
  condominium: string;
  administrator: string;
  status: "active" | "suspended" | "ended";
  unit: "day" | "week" | "month" | "year";
  interval: number;
  anchorOn: string | null;
  anchorOrdinal: number;
  endOn: string | null;
  maxOccurrences: number | null;
  employeeId: string | null;
  durationDays: number;
  billingMode: "per-intervention" | "every-n" | "periodic";
  billingEvery: number;
  billingMonths: number;
  amount: string;
  billingAnchorOn: string | null;
  version: number;
}
export interface RecurringOccurrence {
  id: string;
  planId: string;
  jobId: string;
  ordinal: number;
  dueOn: string;
  scheduledOn: string;
  durationDays: number;
  employeeId: string | null;
  status: "planned" | "completed" | "cancelled";
  performedOn: string | null;
  manualOverride: boolean;
  version: number;
  conflict: boolean;
  hours: string;
  cost: string;
}
export interface RecurringBilling {
  id: string;
  periodKey: string;
  periodFrom: string | null;
  periodTo: string | null;
  amount: string;
  status: "ready" | "invoiced" | "cancelled";
  invoiceId: string | null;
  interventions: number;
}
export interface RecurringDetail {
  plan: RecurringPlan;
  occurrences: RecurringOccurrence[];
  billings: RecurringBilling[];
  invoices: { id: string; number: string | null; amount: string }[];
}
